/*
 * Convert Image to Chart — DeckorateAPI client.
 *
 * Drives POST /v1/deckorate/image/convert-image (DeckorateAPI/routes/v1/
 * convert_chart/chart_conversion.py). The endpoint reads its image from Azure
 * Blob Storage rather than accepting bytes, so a caller uploads first — see
 * `extractChartFromSelection` in chartExtraction.ts, which chains
 * capture -> blob-sas + PUT -> this call.
 *
 * One vision pass reconstructs the data behind a *picture* of a chart and
 * returns an `EditableChart`: type, categories, per-series values, axis
 * titles, legend/label placement, sampled colours and axis scales. Field
 * naming mirrors the FastAPI models exactly (snake_case envelope, e.g.
 * `blob_name`, `chart_type`, `is_stacked`, `slice_colors`).
 *
 * Everything crossing this boundary is normalised before it leaves the
 * module: the wire types are `unknown`, enums outside the union fall back to
 * a known default rather than being trusted, and each series' `values` is
 * padded/trimmed to `categories` so every consumer can index the two together.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { BlobUploadResult } from "../../shared/services/blobImageUpload";
import {
  createConnectorLogger,
  getErrorMessage,
  isAbortError,
  parseHttpError,
  parseJsonErrorBody,
} from "../../shared/services/connectorErrors";
import { newRequestId } from "../../shared/services/requestId";
import { devTrace } from "../../shared/services/devTrace";

/** connectorSystemName tag for all Convert Image to Chart telemetry. */
const CONVERT_CHART_CONNECTOR = "deckorate";

/**
 * The DeckorateAPI route this feature drives.
 *
 * ⚠ The API contract document specifies `POST /api/v1/deckorate/charts/convert`,
 * which is NOT what the service serves today. This value is the deployed one,
 * verified three ways in `apps/backend/ai/DeckorateAPI`:
 *
 *   - `routes/v1/convert_chart/chart_conversion.py` mounts the router at
 *     `prefix="/v1/deckorate/image"` with `@router.post("/convert-image")`;
 *   - `tests/deckorate_api/test_chart_conversion.py` pins
 *     `_URL = "/v1/deckorate/image/convert-image"`;
 *   - `main.py` adds no global prefix and no `root_path`, and
 *     `VITE_DECKORATE_API_BASE_URL` is a bare host — so nothing contributes the
 *     document's `/api` segment, and no other Deckorate client carries one
 *     (compare `/v1/deckorate/image/classify`, `/v1/deckorate/content/blob-sas`).
 *
 * The document's path does appear in the backend's own
 * `models/convert_chart/chart_conversion.py` docstring ("JSON body for POST
 * …/charts/convert"), so the two drifted at some point rather than the document
 * being invented. Sending the documented path today would 404.
 *
 * If the backend is renamed to match the contract, this constant is the only
 * line that changes — and `convertChartApi.test.ts` asserts it, so the rename
 * fails the suite rather than reaching an environment silently (the failure
 * mode §1.1 of docs/coding-standards.md records for three earlier Deckorate
 * paths).
 */
const CONVERT_CHART_PATH = "/v1/deckorate/image/convert-image";

/** The path the API contract document specifies. Exported so the test can state
 *  the divergence as a fact rather than a comment, and so switching over is a
 *  one-line change here plus one in the test. */
export const DOCUMENTED_CONVERT_CHART_PATH = "/api/v1/deckorate/charts/convert";

/** Prefix for this client's correlation ids. */
const REQUEST_ID_PREFIX = "convert-chart-";

/** Scope tag for this feature's dev traces. */
export const CONVERT_CHART_TRACE_SCOPE = "convert-chart";

/** One vision pass over a chart image. Mirrors the 90s budget Convert to
 *  Infographic / Fix Alignment give a single AI call — the extraction is one
 *  completion, so it gets one deadline rather than a share of a longer one. */
export const CONVERT_CHART_REQUEST_TIMEOUT_MS = 90_000;

/** Matches ConvertChartRequest.hint's max_length — trimmed client-side so a
 *  long steer doesn't 422 outright. */
export const MAX_CHART_HINT_CHARS = 500;

/** Reports a Convert Image to Chart failure through the shared telemetry
 *  pipeline (PII-scrubbed, connectorSystemName-tagged). */
export const logConvertChartError = createConnectorLogger(CONVERT_CHART_CONNECTOR, "ConvertChartError");

/* ── Wire contract (mirrors DeckorateAPI/models/convert_chart) ─────────────── */

/** ChartType — the chart's dominant/base type. */
export const CHART_TYPES = ["column", "bar", "line", "area", "pie", "doughnut", "scatter"] as const;
export type ApiChartType = (typeof CHART_TYPES)[number];

/** SeriesRender — how one series is drawn, so a combo chart round-trips. */
export const SERIES_RENDERS = ["column", "bar", "line", "area"] as const;
export type SeriesRender = (typeof SERIES_RENDERS)[number];

/** AxisRef — which value axis a series is plotted against. */
export const AXIS_REFS = ["primary", "secondary"] as const;
export type AxisRef = (typeof AXIS_REFS)[number];

export const LEGEND_POSITIONS = ["bottom", "top", "left", "right"] as const;
export type LegendPosition = (typeof LEGEND_POSITIONS)[number];

/** DataLabelPosition — Office.js names, straight from the model. */
export const DATA_LABEL_POSITIONS = ["outsideEnd", "center", "insideEnd", "insideBase"] as const;
export type DataLabelPosition = (typeof DATA_LABEL_POSITIONS)[number];

/** The type an extraction falls back to when the API sends a `chart_type`
 *  this build doesn't know. Column is the safest rebuild for unrecognised
 *  categorical data, and is the tile the grid pre-selects. */
export const FALLBACK_CHART_TYPE: ApiChartType = "column";

export interface AxisScale {
  readonly min: number | null;
  readonly max: number | null;
  readonly majorUnit: number | null;
}

export interface GridLines {
  readonly majorHorizontal: boolean;
  readonly minorHorizontal: boolean;
  readonly majorVertical: boolean;
  readonly minorVertical: boolean;
}

export interface FontSizes {
  readonly title: number | null;
  readonly axisLabels: number | null;
  readonly dataLabels: number | null;
  readonly legend: number | null;
}

export interface ChartSeries {
  readonly name: string;
  /** Aligned 1:1 with {@link EditableChart.categories}; `null` marks a point
   *  the model could not read off the image. */
  readonly values: ReadonlyArray<number | null>;
  /** `null` follows the chart's base type — only meaningfully different on a
   *  combo chart (e.g. a `line` over columns). */
  readonly renderAs: SeriesRender | null;
  readonly axis: AxisRef;
  readonly lineSmooth: boolean;
  /** 6-hex, no leading '#'. `null` when the colour wasn't sampled. */
  readonly color: string | null;
}

export interface EditableChart {
  readonly chartType: ApiChartType;
  readonly title: string | null;
  readonly categories: readonly string[];
  readonly series: readonly ChartSeries[];
  readonly xAxisTitle: string | null;
  readonly yAxisTitle: string | null;
  readonly secondaryYAxisTitle: string | null;
  readonly isStacked: boolean;
  readonly showLegend: boolean;
  readonly legendPosition: LegendPosition | null;
  readonly showDataLabels: boolean;
  readonly dataLabelPosition: DataLabelPosition | null;
  /**
   * Display hint for the value axis (e.g. "%", "$", "£m"). The values
   * themselves are plain numbers exactly as displayed on the source chart —
   * the extraction lifts the symbol out ("42%" is 42 with `number_format: "%"`).
   *
   * Put back onto the axis ticks by {@link axisNumberFormat}, which also
   * decides when it CANNOT be placed: it is one chart-level field, so on a
   * dual-axis chart there is no way to know which axis it belongs to.
   */
  readonly numberFormat: string | null;
  readonly yAxis: AxisScale | null;
  readonly secondaryYAxis: AxisScale | null;
  /** Pie/doughnut slice fills as 6-hex (no '#'), aligned 1:1 with
   *  `categories`. `null` on every non-pie chart — those carry colour per
   *  series instead. */
  readonly sliceColors: readonly string[] | null;
  readonly gridLines: GridLines | null;
  readonly fontSizes: FontSizes | null;
}

export interface ConvertChartResult {
  readonly requestId: string;
  readonly chart: EditableChart;
  /** Extraction caveats worth showing (estimated values, dropped series,
   *  chart-type approximations). Never fatal. */
  readonly warnings: readonly string[];
  readonly modelId: string | null;
  readonly processingMs: number | null;
}

interface ConvertChartApiRequest {
  request_id: string;
  blob_name: string;
  blob_container?: string;
  hint?: string;
}

/* ── Normalisation ─────────────────────────────────────────────────────────
 * The response is parsed JSON, so every read below narrows rather than casts.
 * Enum lookups use `includes` on a frozen array instead of an object index, so
 * a wire value like "constructor" can't resolve to an inherited
 * Object.prototype member and read as a valid enum.
 */

function asRecord(raw: unknown): Record<string, unknown> {
  return typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
}

function asEnum<T extends string>(raw: unknown, allowed: readonly T[], fallback: T | null): T | null {
  return typeof raw === "string" && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function asString(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim() ? raw : null;
}

function asFiniteNumber(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

function asBoolean(raw: unknown, fallback: boolean): boolean {
  return typeof raw === "boolean" ? raw : fallback;
}

/** 6-hex with no '#'. Anything else (a named colour, "#RGB", a stray object)
 *  is dropped rather than passed on — it would reach PowerPoint's OOXML
 *  `srgbClr val=` attribute, which rejects a malformed value outright. */
const HEX6_PATTERN = /^[0-9a-fA-F]{6}$/;

function asHexColor(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  return HEX6_PATTERN.test(hex) ? hex.toUpperCase() : null;
}

function asStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => (typeof item === "string" ? item : String(item ?? "")));
}

function normaliseAxisScale(raw: unknown): AxisScale | null {
  if (typeof raw !== "object" || raw === null) return null;
  const axis = asRecord(raw);
  const scale: AxisScale = {
    min: asFiniteNumber(axis.min),
    max: asFiniteNumber(axis.max),
    majorUnit: asFiniteNumber(axis.major_unit),
  };
  // An axis object with nothing legible in it is the same as no axis at all —
  // collapsing it here saves every consumer a three-field emptiness check.
  return scale.min === null && scale.max === null && scale.majorUnit === null ? null : scale;
}

function normaliseGridLines(raw: unknown): GridLines | null {
  if (typeof raw !== "object" || raw === null) return null;
  const grid = asRecord(raw);
  return {
    majorHorizontal: asBoolean(grid.major_horizontal, false),
    minorHorizontal: asBoolean(grid.minor_horizontal, false),
    majorVertical: asBoolean(grid.major_vertical, false),
    minorVertical: asBoolean(grid.minor_vertical, false),
  };
}

function normaliseFontSizes(raw: unknown): FontSizes | null {
  if (typeof raw !== "object" || raw === null) return null;
  const fonts = asRecord(raw);
  const sizes: FontSizes = {
    title: asFiniteNumber(fonts.title),
    axisLabels: asFiniteNumber(fonts.axis_labels),
    dataLabels: asFiniteNumber(fonts.data_labels),
    legend: asFiniteNumber(fonts.legend),
  };
  return sizes.title === null && sizes.axisLabels === null && sizes.dataLabels === null && sizes.legend === null
    ? null
    : sizes;
}

/**
 * One series, with `values` forced to `categoryCount` entries.
 *
 * The model is asked for a 1:1 alignment but is reading estimates off pixels,
 * so a short or long row is a realistic response — and every consumer here
 * (the preview renderer, the PptxGenJS data rows) indexes values by category.
 * Padding with `null` and trimming the overflow keeps that invariant true at
 * the boundary rather than at each of them.
 */
function normaliseSeries(raw: unknown, categoryCount: number): ChartSeries {
  const series = asRecord(raw);
  const rawValues: unknown[] = Array.isArray(series.values) ? series.values : [];
  const values: Array<number | null> = [];
  for (let i = 0; i < categoryCount; i++) values.push(asFiniteNumber(rawValues[i]));

  return {
    name: typeof series.name === "string" ? series.name : "",
    values,
    renderAs: asEnum(series.render_as, SERIES_RENDERS, null),
    axis: asEnum(series.axis, AXIS_REFS, "primary") ?? "primary",
    lineSmooth: asBoolean(series.line_smooth, false),
    color: asHexColor(series.color),
  };
}

/** Slice colours are only usable when a full, legible set came back — a
 *  partial set would colour some slices and leave the rest to PowerPoint's
 *  own palette, which reads as a rendering bug rather than extracted branding. */
function normaliseSliceColors(raw: unknown, categoryCount: number): string[] | null {
  if (!Array.isArray(raw) || categoryCount === 0 || raw.length < categoryCount) return null;
  const colors: string[] = [];
  for (let i = 0; i < categoryCount; i++) {
    const hex = asHexColor(raw[i]);
    if (!hex) return null;
    colors.push(hex);
  }
  return colors;
}

export function normaliseChart(raw: unknown): EditableChart {
  const chart = asRecord(raw);
  const categories = asStringArray(chart.categories);
  const rawSeries: unknown[] = Array.isArray(chart.series) ? chart.series : [];

  return {
    chartType: asEnum(chart.chart_type, CHART_TYPES, FALLBACK_CHART_TYPE) ?? FALLBACK_CHART_TYPE,
    title: asString(chart.title),
    categories,
    series: rawSeries.map((series) => normaliseSeries(series, categories.length)),
    xAxisTitle: asString(chart.x_axis_title),
    yAxisTitle: asString(chart.y_axis_title),
    secondaryYAxisTitle: asString(chart.secondary_y_axis_title),
    isStacked: asBoolean(chart.is_stacked, false),
    showLegend: asBoolean(chart.show_legend, true),
    legendPosition: asEnum(chart.legend_position, LEGEND_POSITIONS, null),
    showDataLabels: asBoolean(chart.show_data_labels, false),
    dataLabelPosition: asEnum(chart.data_label_position, DATA_LABEL_POSITIONS, null),
    numberFormat: asString(chart.number_format),
    yAxis: normaliseAxisScale(chart.y_axis),
    secondaryYAxis: normaliseAxisScale(chart.secondary_y_axis),
    sliceColors: normaliseSliceColors(chart.slice_colors, categories.length),
    gridLines: normaliseGridLines(chart.grid_lines),
    fontSizes: normaliseFontSizes(chart.font_sizes),
  };
}

function normaliseResult(raw: unknown, requestId: string): ConvertChartResult {
  const body = asRecord(raw);
  const meta = asRecord(body.meta);
  return {
    requestId: typeof body.request_id === "string" && body.request_id ? body.request_id : requestId,
    chart: normaliseChart(body.chart),
    warnings: Array.isArray(body.warnings)
      ? body.warnings.filter((warning): warning is string => typeof warning === "string" && warning.trim().length > 0)
      : [],
    modelId: asString(meta.model_id),
    processingMs: asFiniteNumber(meta.processing_ms),
  };
}

/* ── Error copy ────────────────────────────────────────────────────────────
 * createHttpClient throws `Deckorate POST /v1/deckorate/image/convert-image:
 * <status> <rawBody>`; describeConvertChartFailure turns that into a sentence
 * worth showing in the sheet — the API's own `message` when it explains
 * something actionable (NOT_A_CHART, BLOB_NOT_FOUND, …), plain-English copy
 * otherwise. Mirrors describeInfographicFailure.
 */

const CONVERT_CHART_HTTP_STATUS = {
  unauthorized: 401,
  forbidden: 403,
  payloadTooLarge: 413,
  unsupportedMedia: 415,
  unprocessable: 422,
  tooManyRequests: 429,
  serverError: 500,
} as const;

export const CONVERT_CHART_UNAUTHORIZED_MESSAGE = "Your session has expired — sign in again and retry.";
export const CONVERT_CHART_BUSY_MESSAGE = "The chart service is busy right now — try again in a moment.";
export const CONVERT_CHART_SERVER_MESSAGE = "The chart service is unavailable right now. Nothing was changed.";
export const CONVERT_CHART_TOO_LARGE_MESSAGE = "That image is too large to read. Try a smaller or cropped one.";
export const CONVERT_CHART_UNSUPPORTED_MESSAGE = "That image format can't be read. Use a PNG, JPEG, WEBP or GIF.";
export const CONVERT_CHART_NOT_A_CHART_MESSAGE =
  "AiVA couldn't find a chart in that image. Select a picture of a chart and try again.";
export const CONVERT_CHART_TIMEOUT_MESSAGE =
  "The chart service took too long to respond. Nothing was changed — try again.";
export const CONVERT_CHART_CANCELLED_MESSAGE = "Reading the chart was cancelled. Nothing was changed.";
export const CONVERT_CHART_FAILED_MESSAGE = "Reading the chart image failed. Nothing was changed — try again.";
/** A 200 whose `chart` carries no plottable point. The request succeeded, so
 *  this is not an HTTP failure — but there is nothing to build a chart from. */
export const CONVERT_CHART_EMPTY_MESSAGE =
  "AiVA read the image but couldn't recover any data from it. Try a clearer chart image.";

/**
 * Longest message worth showing in a toast.
 *
 * The chat toaster auto-dismisses after 3s and renders one line, so a message
 * past roughly this length is gone before it can be read. Generous enough to
 * carry a real explanation ("…an infographic showing multiple example chart
 * types, not a single extractable chart") and short enough to finish reading.
 */
export const MAX_ERROR_MESSAGE_CHARS = 200;

/** How many extraction caveats the sheet lists before it stops. */
export const MAX_SHOWN_WARNINGS = 3;

/**
 * Shape untrusted model prose into a sentence.
 *
 * `message` and `warnings` are written by the vision model, not by the API, so
 * they arrive unshaped: no leading capital, no full stop, sometimes wrapped
 * over several lines, occasionally a paragraph. Rendering that verbatim is what
 * put a lowercase, unpunctuated fragment in front of the user.
 *
 * Returns `null` for anything with no words in it, so callers fall back to
 * their own copy rather than showing an empty toast.
 */
export function normaliseModelMessage(raw: string): string | null {
  // Control characters are replaced rather than stripped so words either side
  // of one do not fuse together; the whitespace collapse then tidies up.
  const printable = Array.from(raw, (char) => {
    const code = char.codePointAt(0) ?? 0;
    return code < 0x20 || code === 0x7f ? " " : char;
  }).join("");
  const collapsed = printable.replace(/\s+/g, " ").trim();
  if (!collapsed) return null;

  const capped =
    collapsed.length > MAX_ERROR_MESSAGE_CHARS
      ? `${collapsed.slice(0, MAX_ERROR_MESSAGE_CHARS - 1).trimEnd()}…`
      : collapsed;
  const sentenced = capped.charAt(0).toUpperCase() + capped.slice(1);
  return /[.!?…]$/.test(sentenced) ? sentenced : `${sentenced}.`;
}

/** The caveats the sheet shows: shaped, de-duplicated and capped. */
export function shownWarnings(warnings: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const warning of warnings) {
    const message = normaliseModelMessage(warning);
    if (message) seen.add(message);
    if (seen.size >= MAX_SHOWN_WARNINGS) break;
  }
  return [...seen];
}

/** Pull `message`/`error.message` out of either error envelope DeckorateAPI returns. */
function convertChartApiErrorMessage(rawBody: string): string | null {
  return parseJsonErrorBody<string>(
    rawBody,
    (parsed) => {
      if (typeof parsed.message === "string" && parsed.message) return normaliseModelMessage(parsed.message);
      const nested = asRecord(parsed.error).message;
      return typeof nested === "string" && nested ? normaliseModelMessage(nested) : null;
    },
    logConvertChartError,
    "convert-chart-error-body-parse",
  );
}

export function describeConvertChartFailure(
  error: unknown,
  fallback: string = CONVERT_CHART_FAILED_MESSAGE,
  options: { readonly timedOut?: boolean } = {},
): string {
  if (isAbortError(error)) {
    return options.timedOut ? CONVERT_CHART_TIMEOUT_MESSAGE : CONVERT_CHART_CANCELLED_MESSAGE;
  }

  const raw = getErrorMessage(error, fallback);
  const parsed = parseHttpError(raw);
  if (!parsed) return raw;

  const { status } = parsed;
  const apiMessage = convertChartApiErrorMessage(parsed.body);

  if (status === CONVERT_CHART_HTTP_STATUS.unauthorized || status === CONVERT_CHART_HTTP_STATUS.forbidden) {
    return CONVERT_CHART_UNAUTHORIZED_MESSAGE;
  }
  if (status === CONVERT_CHART_HTTP_STATUS.payloadTooLarge) return CONVERT_CHART_TOO_LARGE_MESSAGE;
  if (status === CONVERT_CHART_HTTP_STATUS.unsupportedMedia) return CONVERT_CHART_UNSUPPORTED_MESSAGE;
  if (status === CONVERT_CHART_HTTP_STATUS.tooManyRequests) return CONVERT_CHART_BUSY_MESSAGE;
  if (status >= CONVERT_CHART_HTTP_STATUS.serverError) return CONVERT_CHART_SERVER_MESSAGE;
  // 422 NOT_A_CHART is the one 4xx a user can act on without reading an error
  // code, so it gets plain copy when the API sent no sentence of its own.
  if (status === CONVERT_CHART_HTTP_STATUS.unprocessable && !apiMessage) return CONVERT_CHART_NOT_A_CHART_MESSAGE;
  return apiMessage ?? raw;
}

/* ── number_format ─────────────────────────────────────────────────────── */

/** Currency symbols that read as a PREFIX on an axis label ("$8,000"). Anything
 *  else short enough to be a unit reads as a suffix ("7.5%", "1.2M"). */
const CURRENCY_PREFIX_SYMBOLS: ReadonlySet<string> = new Set(["$", "£", "€", "¥", "₹"]);

/** Longest hint still treated as a unit worth putting on every tick. Beyond
 *  this it is a phrase ("billions of USD") that belongs in the axis title,
 *  which the extraction reports separately. */
const MAX_UNIT_SUFFIX_LENGTH = 3;

export interface NumberFormatAffix {
  readonly prefix: string;
  readonly suffix: string;
}

/**
 * `number_format` → where its symbol sits relative to the number.
 *
 * The extraction reports values as plain numbers with the symbol lifted out
 * ("42%" is 42 with `number_format: "%"`, per the extraction prompt), so the
 * symbol has to be put back for the axis to read like the source. Returning an
 * affix rather than a format string keeps the decision in one place: the
 * preview renders it as text and the OOXML builder turns it into an Excel
 * format code, and the two cannot drift.
 *
 * `null` for anything unrecognised — a hint we can't place is left off the
 * ticks rather than guessed at, since a mislabelled axis misstates the data.
 */
export function numberFormatAffix(hint: string | null): NumberFormatAffix | null {
  const trimmed = hint?.trim();
  if (!trimmed) return null;
  // A quote would break out of the literal in an Excel format code.
  if (trimmed.includes('"')) return null;
  if (CURRENCY_PREFIX_SYMBOLS.has(trimmed)) return { prefix: trimmed, suffix: "" };
  if (trimmed.length <= MAX_UNIT_SUFFIX_LENGTH) return { prefix: "", suffix: trimmed };
  return null;
}

/**
 * True when the chart plots against two value axes.
 *
 * `number_format` is a single chart-level field, so on a dual-axis chart there
 * is no way to tell which axis it describes — a source with "$ billion" on the
 * left and "%" on the right reports only one of them. Labelling the wrong axis
 * restates the data ("1.8%" for 1.8 billion dollars), so the format is applied
 * only when there is exactly one value axis to apply it to. Making this
 * per-axis needs `yAxis.number_format` / `secondary_y_axis.number_format` on
 * the API.
 */
export function hasDualValueAxes(chart: EditableChart): boolean {
  return chart.series.some((series) => series.axis === "secondary");
}

/** The affix to put on value-axis ticks, or `null` when it can't be placed
 *  unambiguously. */
export function axisNumberFormat(chart: EditableChart): NumberFormatAffix | null {
  if (hasDualValueAxes(chart)) return null;
  return numberFormatAffix(chart.numberFormat);
}

/* ── Readability rules ─────────────────────────────────────────────────────
 * Two places where reproducing the extraction faithfully produces a WORSE
 * chart than overriding it. Both live here so the preview and the inserted
 * slide make the same call.
 */

/**
 * Above this many marks, data labels overlap into noise.
 *
 * A judgement call, not a spec: ~24 labels (12 categories x 2 series) still
 * read on a slide-width chart, while a 46-year stacked area with six series
 * carries 276 and buries the shape of the data under its own numbers.
 */
export const MAX_READABLE_DATA_LABELS = 30;

/** Pie and doughnut have one entry per category; everything else per series. */
function legendEntryCount(chart: EditableChart): number {
  return chart.chartType === "pie" || chart.chartType === "doughnut"
    ? chart.categories.length
    : chart.series.length;
}

/** Marks that would actually carry a label — unreadable points have none. */
function plottedMarkCount(chart: EditableChart): number {
  if (chart.chartType === "pie" || chart.chartType === "doughnut") {
    const first = chart.series[0];
    return first ? first.values.filter((value) => typeof value === "number").length : 0;
  }
  return chart.series.reduce(
    (total, series) => total + series.values.filter((value) => typeof value === "number").length,
    0,
  );
}

/**
 * Whether the rebuilt chart shows a legend.
 *
 * `show_legend` describes whether the SOURCE had a legend box — and a chart can
 * identify its series without one, by labelling them in place (a stacked area
 * with "VINYL" written across the band, say). PowerPoint has no equivalent of
 * that, so honouring `show_legend: false` there leaves a pile of coloured
 * shapes with nothing to say which is which. A legend is the only way to carry
 * the information across, so anything with more than one thing to tell apart
 * gets one.
 */
export function shouldShowLegend(chart: EditableChart): boolean {
  return chart.showLegend || legendEntryCount(chart) > 1;
}

/**
 * Whether the rebuilt chart prints values on its marks.
 *
 * Capped by {@link MAX_READABLE_DATA_LABELS}. The cap matters because the
 * extraction can report labels that were never numbers: in-band series names
 * read as data labels, and the rebuild then prints a value on every point.
 */
export function shouldShowDataLabels(chart: EditableChart): boolean {
  return chart.showDataLabels && plottedMarkCount(chart) <= MAX_READABLE_DATA_LABELS;
}

/** True when the extracted chart has at least one plottable point — a 200
 *  with empty categories/series is a valid response but an unusable chart. */
export function hasPlottableData(chart: EditableChart): boolean {
  if (chart.categories.length === 0 || chart.series.length === 0) return false;
  return chart.series.some((series) => series.values.some((value) => value !== null));
}

/**
 * POST /v1/deckorate/image/convert-image.
 *
 * `request_id` is a non-secret correlation id echoed back on the response body
 * and the X-Request-Id header; this route *requires* one, so unlike the
 * optional-id endpoints a generated value is always sent.
 */
export async function postConvertChart(
  http: HttpClient,
  blob: BlobUploadResult,
  options: { readonly hint?: string | null; readonly signal?: AbortSignal } = {},
): Promise<ConvertChartResult> {
  const hint = options.hint?.trim();
  const requestId = newRequestId(REQUEST_ID_PREFIX);
  const request: ConvertChartApiRequest = {
    request_id: requestId,
    blob_name: blob.blobName,
    ...(blob.blobContainer ? { blob_container: blob.blobContainer } : {}),
    ...(hint ? { hint: hint.slice(0, MAX_CHART_HINT_CHARS) } : {}),
  };

  const requestOptions: HttpRequestOptions = options.signal ? { signal: options.signal } : {};

  devTrace(CONVERT_CHART_TRACE_SCOPE, "POST image/convert-image", {
    path: CONVERT_CHART_PATH,
    blobName: blob.blobName,
    blobContainer: blob.blobContainer ?? "(api default)",
    hasHint: Boolean(hint),
  });

  try {
    const response = await http.post<unknown>(CONVERT_CHART_PATH, request, requestOptions);
    const result = normaliseResult(response, requestId);
    devTrace(CONVERT_CHART_TRACE_SCOPE, "convert-image responded", {
      chartType: result.chart.chartType,
      categories: result.chart.categories.length,
      series: result.chart.series.length,
      warnings: result.warnings.length,
    });
    return result;
  } catch (error) {
    // Cancellations aren't failures — only report genuine request/parse errors.
    if (!isAbortError(error)) {
      logConvertChartError("convert-chart-request-failed", getErrorMessage(error, "convert-image request failed"));
    }
    throw error;
  }
}
